import * as React from "react";
import { Link, makeStyles, Text } from "@fluentui/react-components";
import { CardSection } from "../../components/CardSection";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        padding: "2rem clamp(1rem, 5vw, 4rem)",
        width: "80%",
        margin: "0 auto",
        maxWidth: "1400px",
    },
});

export const Id = "5f27bdcf-1055-4c66-8a39-eee72ec0a397";
export const Title = "Getting started as Publisher";
export const Subtitle = "Explanation on how to get started";
export const Description = "Find out where to start and which resources to use";
export const Keywords = ["quick", "start", "solution"];

export const Component: React.FC = React.memo(() => {
    const styles = useStyles();

    return (
        <div className={styles.container}>
            <CardSection title="Preface">
                <Text>
                    Getting started with Ianus is fast and easy.
                    <br /><br />
                    After registering your publisher and creating your first product, you're ready to go.
                </Text>
            </CardSection>

            <CardSection title="Creating your publisher">
                <Text>
                    If your organization does not yet own a publisher, you can create one by following these simple steps:
                    <ol>
                        <li>Register using your Microsoft (Entra) login by clicking the <strong>Login</strong> button.</li>
                        <li>Open "Workspace" in the navbar</li>
                        <li>In the side pane, click on "New" below "Publishers"</li>
                        <li>Enter your publisher's name and save. You are free in naming, but beware, this will be visible in generated licenses.</li>
                        <li>For inviting colleagues, instruct them to allow you for adding them, as shown below</li>
                    </ol>
                </Text>
            </CardSection>

            <CardSection title="Join an existing publisher">
                <Text>
                    If your company already has a publisher set up, you’ll need an administrator to add you as a user.  
                    By default, your profile is private, so administrators can’t see or invite you until you make it visible.
                    <br />
                    <br />
                    Here’s how to make your profile visible and connect with your publisher’s admins.  
                    Let’s say your company is called <em>AdventureWorks</em> and employees use the email domain <em>adventureworks.com</em>. The admin’s email might be <em>bilbo.baggins@adventureworks.com</em>.
                    <ol>
                        <li>Click your avatar icon in the top-right navigation bar.</li>
                        <li>Select <strong>Settings</strong>.</li>
                        <li>Enable the checkbox for <strong>Make my profile publicly visible</strong>.</li>
                        <li>Click the "+" button under <strong>Trusted Domains</strong>.</li>
                        <li>Enter the trusted domain (without "@"), e.g., <em>adventureworks.com</em>.</li>
                        <li>Click <strong>Save</strong> to confirm your changes.</li>
                        <li>Ask your administrator to add you by your email address — they’ll now be able to see you.</li>
                    </ol>
                </Text>
            </CardSection>

            <CardSection title="Setup your products">
                Products mirror the modules and components that you want to protect using Ianus Guard.
                In your publisher's workspace, you can maintain them by going to "Products" and creating or updating them.
                You are free in naming, but beware, this will be visible in generated licenses.
            </CardSection>

            <CardSection title="Setup your customers">
                Your first customer will be you yourself, so that you can use Ianus Guard in your development environment.
                In your publisher's workspace, you can maintain customers by going to "Customers" and creating or updating them.
                You are free in naming, but beware, this will be visible in generated licenses.
                <br />
                <br />
                Customers also need to have environments defined so that you can issue licenses for these.
                Environments have a label that you can set freely, it will be visible in generated licenses.
                In addition, the organization Id is needed. Your customer needs to tell it to you, it can be found in the <Link href="https://learn.microsoft.com/en-us/power-platform/admin/determine-org-id-name" target="_blank">Admin Center</Link>.
            </CardSection>

            <CardSection title="Solution & Samples">
                <Text>
                    You can download the Ianus Guard solution preferably via <Link href="https://marketplace.microsoft.com/en-us/product/saas/ianuasoftware.ianus_guard?tab=overview">Microsoft AppSource</Link> or alternatively from our{" "}
                    <Link href="https://github.com/Ianua-Software/Ianus-Clients/blob/main/solutions/IanusGuard_managed.zip" target="_blank">
                        GitHub repository
                    </Link>.
                    The solution features the needed entities, prebuilt PCFs for usage in canvas apps and the custom APIs.
                    <br />
                    <br />
                    After installing the solution, you can already easily embed Ianus Guard with a single clicks into:
                    <ul>
                        <li><strong>Canvas Apps</strong>: Add ian_IanusDataset PCF and configure appropriately</li>
                        <li><strong>Flows</strong>: Call ian_IanusLicenseValidation custom API with your parameters</li>
                        <li><strong>Other components</strong>: Ask your developers to integrate Ianus Guard into your code components</li>
                    </ul>
                    This helps you to quickly integrate Ianus Guard into your Dataverse-based solutions.
                </Text>
            </CardSection>

            <CardSection title="What’s Next?">
                In the next sections of the documentation, you'll find detailed instructions for setting up your Ianus publisher,
                managing products, issuing licenses, and using the DevKit with your apps and services.
            </CardSection>
        </div>
    );
});
